import classNames from 'classnames'
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import useOutsideClick from '../../hooks/use-click-outside'
import styles from './Popup.module.scss'
import type { PopupAlignmentType, PopupPlacementType, PopupProps } from './types'
import { PopupAlignment, PopupPlacement } from './types'

const Popup = ({
  className,
  children,
  trigger,
  onClickOutside = () => {},
  isOpen = true,
  id,
  placement: _placement = PopupPlacement.bottom,
  alignment: _alignment = PopupAlignment.left,
  role,
}: PopupProps) => {
  const [placement, setPlacement] = useState<PopupPlacementType>(_placement)
  const [alignment, setAlignment] = useState<PopupAlignmentType>(_alignment)
  const [customAlignment, setCustomAlignment] = useState<
    undefined | { right?: string; left?: string }
  >()
  const triggerRef = useRef<HTMLDivElement>(null)
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(200) // Default min-width

  const popupRef = useRef<HTMLDivElement>(null)
  const uniqueId = useId()

  useOutsideClick(popupRef, (clickTarget: Node) => {
    if (!triggerRef.current?.contains(clickTarget)) {
      onClickOutside(clickTarget)
    }
  })

  const computePosition = useCallback(() => {
    if (isOpen && triggerRef.current && popupRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      const popupHeight = popupRef.current.offsetHeight // Get the actual height of the popup content
      const popupWidth = popupRef.current.offsetWidth // Get the actual width of the popup content

      setTriggerWidth(triggerRect.width) // Dynamically calculate the trigger's width

      const spaceBelow = window.innerHeight - triggerRect.bottom // Space available below the trigger
      const spaceAbove = triggerRect.top // Space available above the trigger
      const spaceRight = window.innerWidth - triggerRect.right // Space available to the right of the trigger
      const spaceLeft = triggerRect.left // Space available to the left of the trigger

      // Flip the popupRef up if the content height is larger than the available space above / below
      if (
        placement === PopupPlacement.bottom &&
        popupHeight > spaceBelow &&
        spaceAbove > popupHeight
      ) {
        setPlacement('top')
      }
      if (
        placement === PopupPlacement.top &&
        popupHeight > spaceAbove &&
        spaceBelow > popupHeight
      ) {
        setPlacement('bottom')
      }

      if (alignment === PopupAlignment.left) {
        // Slightly move the popup left if the content width is larger than the available space to the right
        const offset = spaceRight + (triggerRect.width ?? 0) - popupWidth
        if (offset < 0) {
          setAlignment('custom')
          setCustomAlignment({
            left: `${offset}px`,
            right: 'unset',
          })
        }
      }

      if (alignment === PopupAlignment.right) {
        // Slightly move the popup right if the content width is larger than the available space to the left
        const offset = spaceLeft + (triggerRect.width ?? 0) - popupWidth
        if (offset < 0) {
          setAlignment('custom')
          setCustomAlignment({
            right: `${offset}px`,
            left: 'unset',
          })
        }
      }
    }
  }, [alignment, isOpen, placement])

  const handleResize = useCallback(() => {
    setAlignment(_alignment)
    setPlacement(_placement)
    computePosition()
  }, [_alignment, _placement, computePosition])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  useLayoutEffect(computePosition)

  return (
    <div className={classNames(styles['pintxo-popup'], className)}>
      {/* Trigger Element */}
      <div
        ref={triggerRef}
        className={'pintxo-popup__trigger'}
        {...(role ? { 'aria-describedby': id || uniqueId } : {})}
      >
        {trigger}
      </div>
      {isOpen ? (
        <div
          ref={popupRef}
          className={classNames('pintxo-popup__content', 'pintxo-elevation-floating')}
          style={{
            // @ts-expect-error TS2353
            '--trigger-width': `${triggerWidth}px`,
            '--alignment-left': `${customAlignment?.left}`,
            '--alignment-right': `${customAlignment?.right}`,
          }} // Dynamically set trigger width CSS variable
          data-placement={placement}
          data-alignment={alignment}
          id={id || uniqueId}
          {...(role ? { role: role } : {})}
        >
          <div className={classNames('pintxo-popup__content__inner')}>{children}</div>
        </div>
      ) : null}
    </div>
  )
}

export default Popup
